import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StatusBar } from "../components/Shell";
import Ico from "../components/Ico";
import { useAppStore } from "../components/AppStore";
import { neighbors, currentUser } from "../mock";
import { jobStatusMeta } from "./NeighborHome";
import { HeartHandshake, ShieldCheck, DollarSign, Clock, CalendarDays, CheckCircle2, Square, CheckSquare, ChevronRight, X, MapPin, Camera } from "lucide-react";

export default function ParentPortal() {
  const navigate = useNavigate();
  const { postedJobs, parentApproveJob, parentDeclineJob, checkins } = useAppStore();
  const [reviewing, setReviewing] = useState(null);
  const [agree, setAgree] = useState(false);
  const [approvedId, setApprovedId] = useState(null);
  const [photoView, setPhotoView] = useState(null);

  const pending = postedJobs.filter((j) => j.status === "kid_accepted");
  const approved = postedJobs.filter((j) => j.status === "parent_approved");

  const openReview = (job) => { setReviewing(job); setAgree(false); };
  const approve = () => {
    if (!agree || !reviewing) return;
    parentApproveJob(reviewing.id);
    setApprovedId(reviewing.id);
    setReviewing(null);
  };

  return (
    <div className="gd-screen bg-[#eef1fb]">
      <div className="flex-shrink-0 px-5 pb-6" style={{ background: "linear-gradient(160deg,#57b24a,#3d8f34)", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
        <StatusBar dark={false} />
        <div className="mt-1 flex items-center gap-2 text-white"><HeartHandshake size={18} /><span className="text-xs font-bold text-white/80">PARENT PORTAL</span></div>
        <h1 className="text-2xl font-extrabold text-white">Hi, {currentUser.parent.split(" ")[0]}</h1>
        <p className="text-sm text-white/75">Approvals for {currentUser.name} sync straight to their dashboard.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white/15 p-3 text-white"><p className="text-2xl font-black">{pending.length}</p><p className="text-[11px] font-semibold text-white/80">Awaiting approval</p></div>
          <div className="rounded-2xl bg-white/15 p-3 text-white"><p className="text-2xl font-black">{approved.length}</p><p className="text-[11px] font-semibold text-white/80">Approved & scheduled</p></div>
        </div>
      </div>

      <div className="gd-scroll px-4 pb-28 gd-stagger">
        <h3 className="mb-2 mt-4 text-base font-extrabold text-[#1f2a52]">Needs Your Approval</h3>
        {pending.length === 0 ? (
          <div className="gd-card flex flex-col items-center p-6 text-center">
            <CheckCircle2 size={36} className="mb-2 text-[#c7cde6]" />
            <p className="text-sm font-semibold text-[#5b648a]">All caught up!</p>
            <p className="mt-1 text-xs text-[#9aa3c0]">When {currentUser.name} accepts a job, it appears here for you to review.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((j) => (
              <button key={j.id} onClick={() => openReview(j)} className="gd-tap flex w-full items-center gap-3 rounded-[20px] bg-white p-3 text-left shadow-[0_8px_22px_rgba(31,42,82,0.06)]">
                <div className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${j.tint}18` }}><Ico name={j.icon} size={22} color={j.tint} /></div>
                <div className="flex-1">
                  <p className="text-sm font-extrabold text-[#1f2a52]">{j.title}</p>
                  <p className="text-[11px] font-semibold text-[#9aa3c0]">{j.schedule} · ${j.pay}</p>
                  <span className="mt-1 inline-block rounded-full bg-[#fdece0] px-2 py-0.5 text-[9px] font-bold text-[#e97f0c]">Kid accepted — review</span>
                </div>
                <ChevronRight size={18} className="text-[#c7cde6]" />
              </button>
            ))}
          </div>
        )}

        <div className="mb-2 mt-6 flex items-center justify-between">
          <h3 className="text-base font-extrabold text-[#1f2a52]">Approved &amp; Scheduled</h3>
          <button onClick={() => navigate("/calendar")} className="text-xs font-bold text-[#3b57c9]">Calendar →</button>
        </div>
        {approved.length === 0 ? (
          <p className="text-[12px] text-[#9aa3c0]">Nothing scheduled yet.</p>
        ) : (
          <div className="space-y-3">
            {approved.map((j) => {
              const c = checkins[j.id] || {};
              const arrived = !!c.arrived, finished = !!c.finished;
              const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
              return (
                <div key={j.id} className="gd-card p-3" style={{ borderLeft: "5px solid #4fae3f" }}>
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${j.tint}18` }}><Ico name={j.icon} size={18} color={j.tint} /></div>
                    <div className="flex-1"><p className="text-[13px] font-bold text-[#1f2a52]">{j.title}</p><p className="text-[11px] font-semibold text-[#9aa3c0]">{j.schedule}</p></div>
                    <span className="rounded-full bg-[#eaf6ea] px-2.5 py-1 text-[10px] font-bold text-[#3d8f34]">Scheduled</span>
                  </div>
                  {/* safe arrival live status */}
                  <div className="mt-2.5 flex items-center gap-2 rounded-2xl bg-[#f7f8fd] px-3 py-2">
                    <ShieldCheck size={15} className={arrived ? "text-[#4fae3f]" : "text-[#c7cde6]"} />
                    <div className="flex flex-1 items-center gap-1.5 text-[10px] font-bold">
                      <span className="rounded-full px-2 py-0.5" style={{ background: arrived ? "#eaf6ea" : "#eef1fb", color: arrived ? "#3d8f34" : "#a3abc9" }}>{arrived ? `Arrived ${fmt(c.arrived)}` : "Not arrived"}</span>
                      <span className="text-[#c7cde6]">→</span>
                      <span className="rounded-full px-2 py-0.5" style={{ background: finished ? "#eef2ff" : "#eef1fb", color: finished ? "#2b3f96" : "#a3abc9" }}>{finished ? `Finished ${fmt(c.finished)}` : "In progress"}</span>
                    </div>
                  </div>
                  {c.photo && (
                    <button onClick={() => setPhotoView(c.photo)} className="gd-tap mt-2 flex w-full items-center gap-2.5 rounded-2xl bg-[#f7f8fd] p-2">
                      <img src={c.photo} alt="Proof" className="h-12 w-12 rounded-xl object-cover" />
                      <span className="flex items-center gap-1 text-[11px] font-bold text-[#3d8f34]"><Camera size={13} /> Photo proof — tap to view</span>
                      <ChevronRight size={16} className="ml-auto text-[#c7cde6]" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* review sheet */}
      {reviewing && (
        <ReviewSheet job={reviewing} agree={agree} setAgree={setAgree} onApprove={approve}
          onDecline={() => { parentDeclineJob(reviewing.id); setReviewing(null); }}
          onClose={() => setReviewing(null)} />
      )}

      {/* approved confirmation */}
      {approvedId && (
        <div className="absolute inset-0 z-50 flex items-end bg-black/40">
          <div className="gd-enter relative w-full rounded-t-[28px] bg-white p-6 pb-8 text-center">
            <button onClick={() => setApprovedId(null)} className="absolute right-5 top-5 text-[#a3abc9]"><X size={20} /></button>
            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#eaf6ea]"><CheckCircle2 size={34} className="text-[#4fae3f]" /></div>
            <h3 className="mt-3 text-xl font-black text-[#1f2a52]">Approved!</h3>
            <p className="mt-1 text-sm text-[#5b648a]">Added to {currentUser.name}'s calendar and the poster's calendar. Tracking &amp; SOS will be active during the job.</p>
            <button onClick={() => { setApprovedId(null); navigate("/calendar"); }} className="gd-blue-btn mt-5 w-full rounded-2xl py-3.5 font-extrabold">View Calendar</button>
            <button onClick={() => setApprovedId(null)} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#5b648a]">Stay in Portal</button>
          </div>
        </div>
      )}

      {/* photo proof lightbox */}
      {photoView && (
        <div className="absolute inset-0 z-[60] flex flex-col items-center justify-center bg-black/85 p-6" onClick={() => setPhotoView(null)}>
          <button onClick={() => setPhotoView(null)} className="absolute right-5 top-6 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white"><X size={20} /></button>
          <p className="mb-3 flex items-center gap-1.5 text-sm font-bold text-white"><Camera size={16} className="text-[#7ee787]" /> Job Photo Proof</p>
          <img src={photoView} alt="Job proof" className="max-h-[70%] w-full rounded-2xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function ReviewSheet({ job, agree, setAgree, onApprove, onDecline, onClose }) {
  const poster = neighbors[job.poster] || { name: "Neighbor", initial: "N", color: "#3b57c9", backgroundCheck: { provider: "Checkr", clearedOn: "2025", items: ["Identity verified", "Criminal search", "Sex offender registry — clear", "Address confirmed"] } };
  const bg = poster.backgroundCheck;
  return (
    <div className="absolute inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="gd-enter max-h-[92%] w-full overflow-y-auto rounded-t-[28px] bg-white p-6 pb-8" style={{ scrollbarWidth: "none" }} onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-[#e6e9f5]" />
        <h3 className="text-lg font-black text-[#1f2a52]">Verify &amp; Approve</h3>
        <p className="text-xs font-semibold text-[#9aa3c0]">{currentUser.name} accepted this job — confirm the details.</p>

        {/* job + verify pay/schedule */}
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#f7f8fd] p-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${job.tint}18` }}><Ico name={job.icon} size={22} color={job.tint} /></div>
          <div className="flex-1"><p className="text-sm font-extrabold text-[#1f2a52]">{job.title}</p><p className="text-[11px] text-[#9aa3c0]">{job.desc}</p></div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <V icon={DollarSign} tint="#4fae3f" v={`$${job.pay}`} l="Pay" />
          <V icon={Clock} tint="#3b57c9" v={job.duration} l="Time" />
          <V icon={CalendarDays} tint="#f5911e" v="Sched." l="Set" />
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-2xl bg-[#eef2ff] px-3 py-2.5 text-[12px] font-bold text-[#2b3f96]"><CalendarDays size={15} /> {job.schedule}</div>

        {/* poster verified */}
        <div className="mt-3 rounded-2xl bg-[#eaf6ea] p-3">
          <div className="flex items-center gap-2 text-sm font-extrabold text-[#2e7d27]"><ShieldCheck size={18} /> Poster background-checked</div>
          <p className="mt-1 text-[11px] font-semibold text-[#5b7a55]">{poster.name} · cleared by {bg.provider} {bg.clearedOn}</p>
        </div>

        {/* accept all liability */}
        <button onClick={() => setAgree(!agree)} className="mt-3 flex items-start gap-2.5 rounded-2xl bg-[#f7f8fd] p-3 text-left">
          {agree ? <CheckSquare size={22} className="mt-0.5 flex-shrink-0 text-[#4fae3f]" /> : <Square size={22} className="mt-0.5 flex-shrink-0 text-[#c7cde6]" />}
          <span className="text-[12px] font-semibold text-[#3a4468]">I have verified the pay and schedule, and I <b>Accept All Liability</b> for {currentUser.name}'s participation in this job.</span>
        </button>

        <button onClick={onApprove} disabled={!agree} className="mt-4 w-full rounded-2xl py-4 text-base font-extrabold text-white transition-all"
          style={{ background: agree ? "linear-gradient(180deg,#57b24a,#3d8f34)" : "#c7cde6", boxShadow: agree ? "0 12px 24px rgba(61,143,52,.35)" : "none" }}>
          Approve &amp; Accept Liability
        </button>
        <button onClick={onDecline} className="mt-2 w-full rounded-2xl py-3 font-bold text-[#e0533d]">Decline</button>
      </div>
    </div>
  );
}
function V({ icon: Icon, tint, v, l }) {
  return (
    <div className="rounded-2xl bg-[#f7f8fd] py-2.5"><Icon size={16} color={tint} className="mx-auto" /><p className="mt-0.5 text-sm font-black text-[#1f2a52]">{v}</p><p className="text-[9px] font-semibold text-[#9aa3c0]">{l}</p></div>
  );
}
